/**
 * Import React libraries.
 */
import * as React from 'react';

/**
 * Import custom styles.
 */
import { BREAKPOINTS, COLORS } from '../styles';
import { Size } from '../types';

/**
 * Import third-party libraries.
 */
import styled from 'styled-components';
import { ComponentXS, ComponentXSStyling, ComponentXXS, ComponentXXSStyling, TypographyBase } from '../styles/typography';
import { SystemIcons } from '../icons';
import {HyperLink, StyledLink} from '../HyperLink';

// Add component-specific styles.
const Label = styled.label`
  ${ComponentXXSStyling(1, COLORS.neutral_500)}

  ${BREAKPOINTS.MEDIUM} {
    ${ComponentXSStyling(1, COLORS.neutral_500)}
  }

  &.small {
    ${ComponentXXSStyling(1, COLORS.neutral_500)}
  }
  &.medium {
    ${ComponentXSStyling(1, COLORS.neutral_500)}
  }

  pointer-events: none;
`;

const LabelRow = styled.div<{ margin?: string }>`
  display: flex;
  align-items: flex-start;
  height: 16px;
  margin-bottom: 4px;
`;

const CopyContainer = styled.div`
  margin-left: auto;

  &:visited:not(.disabled) {
    ${TypographyBase}
    {
      color: ${COLORS.primary_800};
    }
  }

  &:hover:not(.disabled),
  &.hover-state:not(.disabled) {
    ${TypographyBase}
    {
      color: ${COLORS.primary_700};
      text-decoration-line: underline;
    }
  }

  &:focus:not(.disabled),
  &.focus-state:not(.disabled) {
    ${TypographyBase}
    {
      color: ${COLORS.primary_700};
      background-color: white;
      text-decoration-line: underline;
    }
  }

  &:active:not(.disabled),
  &.active-state:not(.disabled) {
    ${TypographyBase}
    {
      color: ${COLORS.primary_700};
      background-color: white;
      text-decoration-line: underline;
    }
  }

  &:not(.disabled)
  {
    ${TypographyBase}
    {
      outline: none;
      padding: 2px 0;
      text-decoration-style: solid;
      text-decoration-line: none;
      font-weight: 700;
      color: ${COLORS.primary_600};
    }
  }

  &.disabled
  {
    ${TypographyBase}
    {
      outline: none;
      padding: 2px 0;
      text-decoration-style: solid;
      text-decoration-line: none;
      font-weight: 700;
      color: ${COLORS.neutral_300};
    }
  }
  cursor: pointer;
`;

const LabelRowInner = styled.div<{ size: Size.Small | Size.Medium | undefined }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  align-items: center;
`;

// Add component-specific types
export type LabelProps = {
  inputId: string;
  text: string;
  size?: Size.Small | Size.Medium;
  margin?: string;
  required?: boolean;
  showCopyButton?: boolean;
};

enum CopyState {
  Disabled = 0,
  Available = 1,
  Copied = 2
}

const InputLabel: React.FunctionComponent<LabelProps> = ({ inputId, text, size, margin, required: requiredProp, showCopyButton }: LabelProps) => {
  // Globally used variables within the component
  const [required, setRequired] = React.useState<boolean>(false);
  const [copyState, setCopyState] = React.useState<CopyState>(CopyState.Available);

  /**
   * Checks if field is set as required and we have to mark it accordingly.
   */
  React.useEffect(() => {
    if(requiredProp === undefined)
    {
      if (document.getElementById(inputId)?.getAttribute('required') === '') {
        setRequired(true);
      } else {
        setRequired(false);
      }
    }
    else
      setRequired(Boolean(requiredProp));
  }, []);
  /**
   * Triggers a click element on a specific element.
   * @param id - ID of the element which needs to be clicked.
   */
  const onTriggerClick = (id: string): void => {
    document.getElementById(id)?.click();
  };

  const copyInputClick = (e: any) => {
    e.preventDefault();
    if(copyState == CopyState.Copied)
      return;
    
    const val = (document.getElementById(inputId) as HTMLInputElement)?.value;
    if(val)
    {
      window.navigator.clipboard.writeText(val);
      setCopyState(CopyState.Copied);
      setTimeout(() => {
        setCopyState(CopyState.Available);
      }, 1000);
    }
  };

  return (
    <LabelRow margin={margin}>
      <LabelRowInner size={size}>
        <Label htmlFor={inputId} onClick={() => onTriggerClick(inputId)} className={size || ''}>
          {text}
        </Label>
        {required && <SystemIcons.Asterisk size="16px" color={COLORS.critical_500} />}

        {showCopyButton && 
        <CopyContainer className={copyState != CopyState.Available ? "disabled" : ""} onClick={copyInputClick}>
          { size == Size.Medium ? <ComponentXS>{copyState == CopyState.Copied ? 'Copied' : 'Copy'}</ComponentXS> :
          <ComponentXXS>{copyState == CopyState.Copied ? 'Copied' : 'Copy'}</ComponentXXS> }
        </CopyContainer> }
      </LabelRowInner>
    </LabelRow>
  );
};

export default InputLabel;
